import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Divider,
    Alert,
    Snackbar,
    Paper,
    Modal,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import WebLayout from "@/components/web/WebLayout";
import CartItem from "@/components/web/Cart/CartItem";
import OrderSuccess from "@/components/web/Cart/OrderSuccess";
import { useCart } from "@/hooks/web/useCart";
import { useOrder } from "@/hooks/web/useOrder";
import { ROUTES } from "@/utils/web/constants";
import Icon from "@/theme/vectors/bundle";

const CartPage = () => {
    const { t } = useTranslation();
    const {
        cartItems,
        updateQuantity,
        removeFromCart,
        getTotalItems,
        getTotalPrice,
    } = useCart();

    const {
        placeOrder,
        loading,
        error,
        orderSuccess,
        orderNumber,
        setOrderSuccessState
    } = useOrder();
    
    const location = useLocation();

    // Scroll to top on initial load
    useEffect(() => {
        const handleScroll = () => {
            const hash = location.hash;
            
            // If there's a hashtag, try to scroll to that element
            if (hash) {
                const targetId = hash.substring(1); // Remove the # character
                const targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                    // Found a valid element with the ID from hashtag
                    setTimeout(() => {
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                } else {
                    // No element found with that ID, scroll to top
                    window.scrollTo(0, 0);
                }
            } else {
                // No hashtag, just scroll to top
                window.scrollTo(0, 0);
            }
        };
        
        // Execute scroll logic after the component is mounted
        handleScroll();
    }, [location]); // Re-run when location changes

    // State cho form thông tin khách hàng
    const [formData, setFormData] = useState({
        fullName: "",
        phone: "",
        address: "",
        notes: "",
    });

    // State cho errors
    const [errors, setErrors] = useState({});
    
    // State for form validity
    const [isFormValid, setIsFormValid] = useState(false);

    // State cho modal đặt hàng
    const [orderModalOpen, setOrderModalOpen] = useState(false);

    // State cho thông báo
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Format giá tiền
    const formatPrice = (price) => {
        return new Intl.NumberFormat("vi-VN", {
            style: "currency",
            currency: "VND",
        }).format(price);
    };

    // Vietnamese phone number regex pattern
    const phoneRegex = /^([(]?(0|84|\+84)[)]?)([.-]*[^\S\r\n\t]*)?(3[2-9]|5[25689]|7[0-9]|8[1-9]|9[0-9])([.-]*[^\S\r\n\t]*\d){7}$/;

    // Xử lý input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Validate on type
        let newErrors = { ...errors };
        
        if (name === 'fullName') {
            if (!value.trim()) {
                newErrors.fullName = t("validation.fullNameRequired");
            } else {
                delete newErrors.fullName;
            }
        }
        
        if (name === 'phone') {
            if (!value.trim()) {
                newErrors.phone = t("validation.phoneRequired");
            } else if (!phoneRegex.test(value)) {
                newErrors.phone = t("validation.invalidPhone");
            } else {
                delete newErrors.phone;
            }
        }
        
        if (name === 'address') {
            if (!value.trim()) {
                newErrors.address = t("validation.addressRequired");
            } else if (value.trim().length < 5) {
                newErrors.address = t("validation.addressMinLength", "Địa chỉ phải có ít nhất 5 ký tự");
            } else {
                delete newErrors.address;
            }
        }
        
        setErrors(newErrors);
        
        // Check if the form is valid
        const isValid = 
            formData.fullName.trim() !== "" && 
            (name === 'fullName' ? value.trim() !== "" : true) &&
            formData.address.trim() !== "" && 
            (name === 'address' ? value.trim().length >= 5 : formData.address.trim().length >= 5) &&
            (name === 'phone' ? phoneRegex.test(value) : phoneRegex.test(formData.phone)) &&
            Object.keys(newErrors).length === 0;
            
        setIsFormValid(isValid);
    };

    // Validate form
    const validate = () => {
        const newErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = t("validation.fullNameRequired");
        }

        if (!formData.phone.trim()) {
            newErrors.phone = t("validation.phoneRequired");
        } else if (!phoneRegex.test(formData.phone)) {
            newErrors.phone = t("validation.invalidPhone");
        }

        if (!formData.address.trim()) {
            newErrors.address = t("validation.addressRequired");
        } else if (formData.address.trim().length < 5) {
            newErrors.address = t("validation.addressMinLength", "Địa chỉ phải có ít nhất 5 ký tự");
        }

        setErrors(newErrors);
        const isValid = Object.keys(newErrors).length === 0;
        setIsFormValid(isValid);
        return isValid;
    };

    // Xử lý submit form thông tin
    const handleSubmit = (e) => {
        e.preventDefault();

        if (validate()) {
            setOrderModalOpen(true);
        }
    };

    // Xử lý đặt hàng
    const handlePlaceOrder = async () => {
        try {
            // Call the placeOrder function from useOrder hook
            const result = await placeOrder({
                fullName: formData.fullName,
                phone: formData.phone,
                address: formData.address,
                notes: formData.notes
            });

            if (result.success) {
                // Close the modal first
                setOrderModalOpen(false);
                
                // Show success notification
                setSnackbar({
                    open: true,
                    message: t("cart.orderPlaced"),
                    severity: "success",
                });
                
                // Wait for 3 seconds before showing the order success page
                // This delay allows the user to see the toast notification
                setOrderSuccessState(true, 3000);
            } else {
                // Show error notification
                setSnackbar({
                    open: true,
                    message: result.error || t("cart.orderError"),
                    severity: "error",
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: t("cart.orderError"),
                severity: "error",
            });
        }
    };

    // Đóng modal đặt hàng
    const handleCloseOrderModal = () => {
        setOrderModalOpen(false);
    };

    // Đóng snackbar
    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    };

    return (
        <WebLayout>
            {orderSuccess ? (
                <OrderSuccess orderNumber={orderNumber} />
            ) : (
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {t("cart.title")}
                    </Typography>

                    {/* Giỏ hàng trống */}
                    {cartItems.length === 0 ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 6,
                                textAlign: "center",
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 2,
                                mb: 4,
                            }}
                        >
                            <Icon name="cart" size={64} color="#ccc" />

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                {t("cart.empty")}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                paragraph
                            >
                                {t("cart.emptyDescription")}
                            </Typography>

                            <Button
                                variant="contained"
                                component={Link}
                                to={ROUTES.PRODUCTS}
                                startIcon={<Icon name="shopping" />}
                                sx={{ mt: 2 }}
                            >
                                {t("cart.continueShopping")}
                            </Button>
                        </Paper>
                    ) : (
                        <Box sx={{ mb: 4 }}>
                            {/* Tiêu đề giỏ hàng */}
                            <Typography variant="h6" gutterBottom>
                                {t("cart.items", { count: getTotalItems() })}
                            </Typography>

                            {/* Danh sách sản phẩm */}
                            {cartItems.map((item) => (
                                <CartItem
                                    key={item.variantId}
                                    item={item}
                                    onUpdateQuantity={updateQuantity}
                                    onRemove={removeFromCart}
                                />
                            ))}

                            {/* Tổng tiền */}
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'flex-end', 
                                    mt: 2 
                                }}
                            >
                                <Typography variant="h6">
                                    {t("cart.subtotal")}: {formatPrice(getTotalPrice())}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 4 }} />

                            {/* Form thông tin người nhận */}
                            <Typography variant="h5" component="h2" gutterBottom>
                                {t("cart.deliveryInfo")}
                            </Typography>

                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                noValidate
                                sx={{ mt: 3 }}
                            >
                                <Grid container spacing={3}>
                                    {/* Họ và tên */}
                                    <Grid item xs={12}>
                                        <TextField
                                            label={`${t("cart.fullName")} *`}
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            error={!!errors.fullName}
                                            helperText={errors.fullName}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    {/* Số điện thoại */}
                                    <Grid item xs={12}>
                                        <TextField
                                            label={`${t("cart.phone")} *`}
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            error={!!errors.phone}
                                            helperText={errors.phone}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    {/* Địa chỉ giao hàng */}
                                    <Grid item xs={12}>
                                        <TextField
                                            label={`${t("cart.address")} *`}
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            error={!!errors.address}
                                            helperText={errors.address || t("validation.addressMinLengthInfo", "Tối thiểu 5 ký tự")}
                                            required
                                            fullWidth
                                            multiline
                                            rows={3}
                                            placeholder={t("cart.addressPlaceholder", "Vui lòng nhập đầy đủ địa chỉ (ít nhất 5 ký tự)")}
                                        />
                                    </Grid>

                                    {/* Ghi chú */}
                                    <Grid item xs={12}>
                                        <TextField
                                            label={t("cart.notes")}
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            fullWidth
                                            multiline
                                            rows={3}
                                        />
                                    </Grid>

                                    {/* Nút xác nhận */}
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            sx={{ mt: 2, py: 1.5 }}
                                            disabled={loading || !isFormValid}
                                        >
                                            {t("cart.confirmOrder")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    )}

                    {/* Tổng kết giỏ hàng */}
                    {cartItems.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Typography variant="h6" component="h3" gutterBottom>
                                {t("cart.summary")}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>{t("cart.items", { count: getTotalItems() })}</Typography>
                                <Typography>{getTotalItems()}</Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>{t("cart.subtotal")}:</Typography>
                                <Typography>{formatPrice(getTotalPrice())}</Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography fontWeight="bold">{t("cart.total")}:</Typography>
                                <Typography fontWeight="bold" color="primary.main">
                                    {formatPrice(getTotalPrice())}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Container>
            )}

            {/* Modal xác nhận đơn hàng */}
            <Modal
                open={orderModalOpen}
                onClose={!loading ? handleCloseOrderModal : undefined}
                aria-labelledby="order-confirmation-modal"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: "70%", md: "50%" },
                        maxWidth: "600px",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        maxHeight: "90vh",
                        overflow: "auto",
                    }}
                >
                    {loading ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4
                        }}>
                            <Icon name="spinner" spin={true} size={40} sx={{ mb: 2 }} />
                            <Typography variant="h6" align="center">
                                {t("cart.processingOrder")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                {t("cart.pleaseWait")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="h5" mb={2}>
                                {t("cart.confirmOrder")}
                            </Typography>
                            
                            <Box mb={3}>
                                <Typography variant="h6" mb={1} sx={{ fontWeight: 'bold' }}>
                                    {t("cart.customerInfo")}
                                </Typography>
                                <TableContainer component={Paper} sx={{ mb: 2 }}>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                                                    {t("cart.fullName")}
                                                </TableCell>
                                                <TableCell>{formData.fullName}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    {t("cart.phone")}
                                                </TableCell>
                                                <TableCell>{formData.phone}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    {t("cart.address")}
                                                </TableCell>
                                                <TableCell>{formData.address}</TableCell>
                                            </TableRow>
                                            {formData.notes && (
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        {t("cart.notes")}
                                                    </TableCell>
                                                    <TableCell>{formData.notes}</TableCell>
                                                </TableRow>
                                            )}
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    {t("cart.paymentMethod")}
                                                </TableCell>
                                                <TableCell>{t("cart.cod")}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                
                                <Typography variant="h6" mb={1} sx={{ fontWeight: 'bold' }}>
                                    {t("cart.orderSummary")}
                                </Typography>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t("cart.product")}</TableCell>
                                                <TableCell align="right">{t("cart.quantity")}</TableCell>
                                                <TableCell align="right">{t("cart.price")}</TableCell>
                                                <TableCell align="right">{t("cart.total")}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {cartItems.map((item) => (
                                                <TableRow key={item.variantId}>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {item.productName}
                                                        </Typography>
                                                        {item.attributeSummary && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {item.attributeSummary}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">{formatPrice(item.price)}</TableCell>
                                                    <TableCell align="right">{formatPrice(item.price * item.quantity)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                                    {t("cart.total")}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                    {formatPrice(getTotalPrice())}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCloseOrderModal}
                                    disabled={loading}
                                >
                                    {t("common.cancel")}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handlePlaceOrder}
                                    disabled={loading}
                                >
                                    {t("cart.confirmPlace")}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* Thông báo */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ 
                    '& .MuiAlert-filledSuccess': {
                        fontSize: '1rem'
                    }
                }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </WebLayout>
    );
};

export default CartPage;
